import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatRupiah } from "@/lib/api";
import { GROUPS, defaultSel, makeLine, selError } from "@/lib/options";

const Choice = ({ active, onClick, name, price, testId, soldOut }) => (
  <button type="button" onClick={onClick} disabled={soldOut} aria-pressed={active} data-testid={testId}
    className={`flex items-center justify-between rounded-lg border-2 px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${active ? "border-emerald-600 bg-emerald-50 font-semibold text-emerald-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
    <span className={soldOut ? "line-through" : ""}>{name}</span>
    {soldOut
      ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700" data-testid={`${testId}-soldout`}>Habis</span>
      : <span className="font-mono text-xs">{price ? `+${formatRupiah(price)}` : "Gratis"}</span>}
  </button>
);

export const OptionPicker = ({ product, onClose, onAdd }) => {
  const [sel, setSel] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => { if (product) { setSel(defaultSel(product)); setQty(1); } }, [product]);

  if (!product || !sel) return null;
  const line = makeLine(product, sel, qty);

  const toggle = (g, i) => setSel((s) => g.multi
    ? { ...s, toppings: s.toppings.includes(i) ? s.toppings.filter((x) => x !== i) : [...s.toppings, i] }
    : { ...s, [g.key]: s[g.key] === i && !product[g.key].required ? null : i });

  const submit = () => {
    const err = selError(product, sel);
    if (err) return toast.error(err);
    onAdd(sel, qty);
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto" aria-describedby={undefined} data-testid="option-picker">
        <DialogTitle className="pr-6 font-display leading-snug">{product.name}</DialogTitle>
        <div className="mt-1" />
        {GROUPS.filter((g) => product[g.key]?.enabled && product[g.key].items?.length).map((g) => (
          <div key={g.key} className="space-y-2" data-testid={`option-group-${g.key}`}>
            <p className="text-sm font-semibold text-slate-800">
              {g.label} <span className="text-xs font-normal text-slate-500">{g.multi ? "(boleh lebih dari satu, opsional)" : product[g.key].required ? "(wajib pilih satu)" : "(opsional)"}</span>
            </p>
            <div className="grid gap-2">
              {product[g.key].items.map((it, i) => (
                <Choice key={i} name={it.name} price={it.price} testId={`option-${g.key}-${i}`} soldOut={it.available === false}
                  active={g.multi ? sel.toppings.includes(i) : sel[g.key] === i} onClick={() => toggle(g, i)} />
              ))}
            </div>
          </div>
        ))}
        <div className="flex items-center justify-between border-t border-slate-100 pt-4">
          <div className="flex items-center gap-2">
            <Button type="button" size="icon" variant="outline" className="h-8 w-8" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="option-qty-decrease"><Minus className="h-3 w-3" /></Button>
            <span className="w-6 text-center font-mono font-bold" data-testid="option-qty">{qty}</span>
            <Button type="button" size="icon" variant="outline" className="h-8 w-8" onClick={() => setQty((q) => Math.min(99, q + 1))} data-testid="option-qty-increase"><Plus className="h-3 w-3" /></Button>
          </div>
          <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="option-add-button">
            Tambah &middot; <span className="ml-1 font-mono" data-testid="option-line-total">{formatRupiah(line.price * qty)}</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
